/**
 * Page "Messages du formateur" — portail étudiant.
 * Liste les messages reçus (individuels + broadcasts de classe).
 */
import { useEffect, useState } from 'react';
import {
  MessageSquare, Calendar, User, BookOpen, Users as UsersIcon, Loader2,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi } from '../services/portailApi';

interface MessageItem {
  id: number;
  formateur_nom: string;
  portee: 'individuel' | 'classe';
  classe: string;
  sujet: string;
  contenu: string;
  matiere_libelle: string;
  date_envoi: string;
  lu: boolean;
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function MessagesFormateur() {
  const [items, setItems] = useState<MessageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [active, setActive] = useState<MessageItem | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await portailApi.getMessagesFormateur();
        setItems(r.results || []);
      } catch (e: any) {
        setError(e?.message || 'Impossible de charger les messages.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <PortailLayout showHeader title="Messages des formateurs">
      {loading && (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-400">
          <Loader2 size={16} className="animate-spin" /> Chargement…
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-900/30 border border-red-800 text-red-200 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
          <MessageSquare size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-500" />
          <div className="text-sm font-medium text-gray-300">Aucun message reçu</div>
          <p className="text-xs text-gray-500 mt-1">
            Les messages envoyés par vos formateurs apparaîtront ici.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map(m => (
            <button key={m.id} onClick={() => setActive(m)}
              className={`w-full text-left bg-gray-900 border rounded-xl p-3 transition ${
                m.lu
                  ? 'border-gray-800 hover:border-gray-700'
                  : 'border-blue-800/50 hover:border-blue-600'
              }`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold inline-flex items-center gap-1 ${
                    m.portee === 'classe'
                      ? 'bg-purple-900/40 text-purple-300'
                      : 'bg-blue-900/40 text-blue-300'
                  }`}>
                    {m.portee === 'classe'
                      ? <><UsersIcon size={9} /> Classe</>
                      : <><User size={9} /> Direct</>}
                  </span>
                  {!m.lu && <span className="w-2 h-2 rounded-full bg-blue-500" />}
                </div>
                <span className="text-[10px] text-gray-500 flex-shrink-0">{fmtDate(m.date_envoi)}</span>
              </div>
              {m.sujet && (
                <div className="font-semibold text-sm text-gray-100 truncate">{m.sujet}</div>
              )}
              <div className="text-xs text-gray-300 truncate mt-0.5">{m.contenu}</div>
              <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1"><User size={10} /> {m.formateur_nom}</span>
                {m.matiere_libelle && (
                  <span className="inline-flex items-center gap-1">
                    <BookOpen size={10} /> {m.matiere_libelle}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {active && <ModalMessage m={active} onClose={() => setActive(null)} />}
    </PortailLayout>
  );
}

function ModalMessage({ m, onClose }: { m: MessageItem; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-gray-900 border-b border-gray-700 px-4 py-3 flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                m.portee === 'classe'
                  ? 'bg-purple-900/40 text-purple-300'
                  : 'bg-blue-900/40 text-blue-300'
              }`}>
                {m.portee === 'classe' ? 'Tous les étudiants' : 'Message direct'}
              </span>
            </div>
            <h3 className="font-bold text-base text-gray-100">{m.sujet || '(Sans sujet)'}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">✕</button>
        </header>
        <div className="p-4 space-y-3">
          <div className="text-xs text-gray-400 space-y-0.5">
            <div className="inline-flex items-center gap-1.5"><User size={11} /> {m.formateur_nom}</div>
            {m.matiere_libelle && (
              <div className="inline-flex items-center gap-1.5 ml-3"><BookOpen size={11} /> {m.matiere_libelle}</div>
            )}
            <div className="inline-flex items-center gap-1.5 ml-3"><Calendar size={11} /> {fmtDate(m.date_envoi)}</div>
          </div>
          <div className="border-t border-gray-700 pt-3 text-sm text-gray-100 whitespace-pre-wrap leading-relaxed">
            {m.contenu}
          </div>
        </div>
      </div>
    </div>
  );
}
